/**
 * @file api-legal-targets.ts
 * @description Документы и группы магазина как цели ссылок (phase19.16,
 * партия «Юридические документы»; db-design-legal-docs §4, §6;
 * uiux-legal-steps-b-c §8): короткая строка цели для «Куда ведёт» пункта
 * меню и кнопки секции, для окна «+ Добавить ссылку на документ» подвала и
 * пометок своих строк «Ссылок».
 *
 * Тонкая обёртка над клиентом «Юридических документов» (`api-legal.ts`):
 * запрос и разбор ответа (в том числе прежней формы) — там, здесь только
 * выбор полей цели и общий на вкладку список (`legal-targets-store`).
 */

import type { LegalDocumentListItemDto, LegalGroupSource } from "@chaiberi/shared";

import { createCertificatesGroup as createGroup, listLegalDocs } from "./api-legal";
import { cachedLegalTargets, rememberLegalTargets } from "./site-view/legal-targets-store";
import { getTabContextId } from "./tab-context";

/** Документ или группа как цель ссылки. */
export type LegalTarget = Pick<LegalDocumentListItemDto, "id" | "slug" | "title" | "kind" | "status" | "visibleOnSite" | "groupSource" | "members">;

type TargetSource = Pick<LegalDocumentListItemDto, "id" | "slug" | "title" | "kind" | "status" | "visibleOnSite"> & {
  groupSource?: LegalGroupSource | null;
  members?: { total: number; visible: number } | null;
};

const toTarget = (item: TargetSource): LegalTarget => ({
  id: item.id,
  slug: item.slug,
  title: item.title,
  kind: item.kind,
  status: item.status,
  visibleOnSite: item.visibleOnSite,
  groupSource: item.groupSource ?? null,
  members: item.members ?? null,
});

/** Все документы и группы магазина (черновики тоже — им нужна пометка «не опубликован»). */
export async function listLegalTargets(): Promise<LegalTarget[]> {
  return (await listLegalDocs()).items.map(toTarget);
}

/** Общий на вкладку список (один запрос на всех, пока не сброшен); `force` — запросить заново. */
export function loadLegalTargets(force = false): Promise<LegalTarget[]> {
  const key = getTabContextId() ?? "";
  const cached = force ? null : cachedLegalTargets(key);
  return cached ?? rememberLegalTargets(key, listLegalTargets());
}

/**
 * Создать автоматическую группу сертификатов (SB141): сервер создаёт её
 * сразу опубликованной. `published` — видна ли она уже на сайте: без
 * сертификатов с файлом группа «пуста» и появится сама, когда они будут.
 */
export async function createCertificatesGroup(): Promise<{ target: LegalTarget; published: boolean }> {
  const doc = await createGroup();
  const members = doc.members ? { total: doc.members.length, visible: doc.members.filter((m) => m.visibleOnSite).length } : null;
  const target = toTarget({ ...doc, members });
  return { target, published: target.visibleOnSite };
}
